import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_8S } from '@modules/common/constants';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getOAuthIntegrationDataInput,
  getSuccessMessageForAddOrEditIntegration,
} from '@modules/integrationsV2/utils';
import { decodeOAuthState } from '@modules/integrationsV2/utils/decodeOAuthState';
import Analytics from 'common/Analytics';
import {
  GetTenantByIdDocument,
  GetUserByEmailDocument,
  IntegrationDirection,
  useCreateIntegrationMutation,
} from 'generated/graphql';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

interface CreateOAuthIntegrationOptions {
  redirectUri: string;
  onCompleted?: (integrationID: string) => void;
  shouldRedirect?: boolean;
}

export const useCreateOAuthIntegration = ({
  redirectUri,
  onCompleted,
  shouldRedirect = true,
}: CreateOAuthIntegrationOptions) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const navigate = useNavigate();

  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const { search, hash } = window.location;

  const hashedParams = new URLSearchParams(hash.slice(1));
  const accessToken = hashedParams.get('access_token');
  const hashState = hashedParams.get('state');
  const decodedHashState = hashState ? decodeOAuthState(hashState) : null;

  const searchParams = new URLSearchParams(search);
  const code = searchParams.get('integration_code');
  const queryState = searchParams.get('state');
  const domainLocation = searchParams.get('location');
  const accountsServer = searchParams.get('accounts-server');
  const decodedQueryState = queryState ? decodeOAuthState(queryState) : null;

  // TODO: Ideally we should only deconstruct the params required for that
  // integration type rather than deconstructing all of them here

  const direction = decodedQueryState?.direction || decodedHashState?.direction;

  const channelType =
    decodedQueryState?.channelType || decodedHashState?.channelType;

  const integrationType =
    decodedQueryState?.integrationType || decodedHashState?.integrationType;

  const authCode = code || accessToken;

  const clientID =
    decodedQueryState?.data?.clientID?.toString() ||
    decodedHashState?.data?.clientID?.toString();

  const clientSecret =
    decodedQueryState?.data?.clientSecret?.toString() ||
    decodedHashState?.data?.clientSecret?.toString();

  const dataCenter =
    decodedQueryState?.data?.dataCenter?.toString() ||
    decodedHashState?.data?.dataCenter?.toString();

  const subDomain =
    decodedQueryState?.data?.subDomain?.toString() ||
    decodedHashState?.data?.subDomain?.toString() ||
    domainLocation;

  const description =
    decodedQueryState?.data?.description?.toString() ||
    decodedHashState?.data?.description?.toString();

  const [createIntegrationMutation, { loading, error, called }] =
    useCreateIntegrationMutation({
      refetchQueries: [GetTenantByIdDocument, GetUserByEmailDocument],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        const integrationID = data?.createIntegration?.id ?? '';

        if (onCompleted) {
          onCompleted(integrationID);
        }

        if (
          direction === IntegrationDirection.Inbound &&
          channelType &&
          integrationType
        ) {
          dispatch({
            type: IntegrationsActionType.SET_VIEW,
            payload: {
              selectedIntegrationSourceOption: {
                channelType,
                integrationType,
                integrationID,
              },
            },
          });
        }

        const successMessage =
          channelType && integrationType
            ? getSuccessMessageForAddOrEditIntegration({
                channelType,
                integrationType,
                action: 'add',
              })
            : null;

        if (successMessage && direction === IntegrationDirection.Inbound) {
          showSuccessNotification({
            contentComponent: () => successMessage,
            props: {
              isClosable: true,
              timeout: NOTIFICATION_TIMEOUT_LONG_8S,
            },
          });
        }

        Analytics.track(
          `${channelType ?? 'Integration'} connection successful`,
        );

        if (shouldRedirect) {
          navigate(redirectUri);
        }
      },
      onError: (error) => {
        showErrorNotification({
          contentComponent: `Something went wrong${
            error.message ? `: ${error.message}` : ''
          }`,
          props: {},
        });
        Analytics.track(`${channelType ?? 'Integration'} connection failed`);
      },
    });

  const createOAuthIntegration = useCallback(async () => {
    if (!authCode || !channelType || !integrationType || !direction) {
      return;
    }

    try {
      await createIntegrationMutation({
        variables: {
          tenantID,
          createdByUser: userID,
          channel: channelType,
          integrationType,
          direction,
          data: getOAuthIntegrationDataInput({
            channelType,
            authCode,
            clientID: clientID ?? '',
            clientSecret: clientSecret ?? '',
            dataCenter: dataCenter ?? '',
            subDomain: subDomain ?? '',
            accountsServer: accountsServer ?? '',
            redirectUri,
          }),
          integrationDescription: description
            ? {
                description,
              }
            : undefined,
        },
      });
    } catch {
      // do nothing, already handled from onError
    }
  }, [
    accountsServer,
    authCode,
    channelType,
    createIntegrationMutation,
    description,
    direction,
    integrationType,
    redirectUri,
    clientID,
    clientSecret,
    dataCenter,
    subDomain,
    tenantID,
    userID,
  ]);

  useEffect(() => {
    if (called) {
      return;
    }
    createOAuthIntegration();
  }, [called, createOAuthIntegration]);

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  return IsLoading(status);
};
